import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { t } = useI18n();
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const data = await login(email, password);
            if (data.user.role === "crn_admin") nav("/admin");
            else if (data.salon && !data.salon.onboarding_completed) nav("/onboarding");
            else nav("/app");
        } catch (err) {
            setError(formatApiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div className="grid min-h-screen md:grid-cols-2">
            <div className="flex flex-col justify-center px-6 py-12 sm:px-12 lg:px-20">
                <div className="mx-auto w-full max-w-md">
                    <div className="mb-10 flex items-center justify-between"><Logo /><LanguageSwitcher compact /></div>
                    <h1 className="font-serif text-3xl font-bold tracking-tight">{t("auth.loginTitle")}</h1>
                    <p className="mt-2 text-muted-foreground">{t("auth.loginSub")}</p>
                    <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
                        <div><Label>{t("common.email")}</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="mt-1.5" /></div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>{t("common.password")}</Label>
                                <Link to="/forgot-password" className="text-xs font-medium text-accent hover:underline" data-testid="forgot-link">{t("auth.forgot")}</Link>
                            </div>
                            <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="mt-1.5" />
                        </div>
                        {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
                        <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit">
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.signIn")}
                        </Button>
                    </form>
                    <p className="mt-6 text-sm text-muted-foreground">
                        {t("auth.noAccount")} <Link to="/register" className="font-semibold text-accent hover:underline">{t("common.signUp")}</Link>
                    </p>
                </div>
            </div>
            <div className="relative hidden md:block">
                <img src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f" alt="Salon" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-primary/40" />
            </div>
        </div>
    );
}
